import { useState, useEffect, useRef } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandList, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@uidotdev/usehooks';
import { supabase } from '@/integrations/supabase/client';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface Prediction {
  placePrediction: {
    placeId: string;
    text: { text: string };
    structuredFormat?: {
      mainText: { text: string };
      secondaryText: { text: string };
    };
  };
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = "Entrez une adresse...",
  disabled = false,
  className,
  id,
}: AddressAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const debouncedValue = useDebounce(value, 300);

  // Fetch predictions when input changes
  useEffect(() => {
    if (debouncedValue.length < 3) {
      setPredictions([]);
      return;
    }

    const fetchPredictions = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('google-places-autocomplete', {
          body: { input: debouncedValue },
        });
        
        if (error) {
          logger.error("AddressAutocomplete", "Error fetching predictions:", error);
          setPredictions([]);
        } else {
          setPredictions(data?.suggestions || []);
        }
      } catch (error) {
        logger.error("AddressAutocomplete", "Error fetching predictions:", error);
        setPredictions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPredictions();
  }, [debouncedValue]);

  const handleSelect = (text: string) => {
    onChange(text);
    setIsOpen(false);
    setPredictions([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!isOpen && e.target.value.length >= 3) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (value.length >= 3) {
      setIsOpen(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Delay close to allow clicking on suggestions
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
      }
    }, 200);
  };

  const showDropdown = isOpen && value.length >= 3 && (isLoading || predictions.length > 0);

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        id={id}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(className)}
        autoComplete="off"
      />
      
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 z-[150] mt-1">
          <Command shouldFilter={false} className="rounded-md border border-border bg-popover">
            <CommandList className="max-h-[200px]">
              {isLoading ? (
                <div className="flex items-center justify-center p-4 text-body-md text-muted-foreground">
                  <div className="h-4 w-4 animate-spin rounded-full border border-current border-t-transparent mr-2" />
                  Recherche...
                </div>
              ) : predictions.length === 0 ? (
                <CommandEmpty className="py-4 text-center text-body-md text-muted-foreground">
                  Aucune adresse trouvée
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {predictions.map((prediction) => (
                    <CommandItem
                      key={prediction.placePrediction.placeId}
                      value={prediction.placePrediction.text.text}
                      onSelect={() => handleSelect(prediction.placePrediction.text.text)}
                      className="cursor-pointer flex items-start gap-2 px-3 py-2"
                    >
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div className="flex flex-col">
                        {prediction.placePrediction.structuredFormat ? (
                          <>
                            <span className="font-medium">
                              {prediction.placePrediction.structuredFormat.mainText.text}
                            </span>
                            <span className="text-helper text-muted-foreground">
                              {prediction.placePrediction.structuredFormat.secondaryText.text}
                            </span>
                          </>
                        ) : (
                          <span>{prediction.placePrediction.text.text}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
}
